import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { UpdateEmailReadStatusParams } from "./types";

/**
 * Composable for toggling email read/unread status with optimistic updates.
 */
export function useUpdateEmailReadStatus() {
  const queryClient = useQueryClient();

  const updateReadStatusMutation = useMutation({
    mutationFn: async (params: UpdateEmailReadStatusParams) => {
      if (params.isThread && params.threadId) {
        await Unipile.updateThread(
          params.threadId,
          { unread: params.markAsUnread },
          params.accountId
        );
      } else {
        await Unipile.updateEmail(
          params.emailId,
          { unread: params.markAsUnread },
          params.accountId
        );
      }
      return params;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["email-conversations"] });

      // Snapshot previous conversations
      const previousConversations = queryClient.getQueriesData<any[]>({
        queryKey: ["email-conversations"],
      });

      // Optimistically update conversations cache
      queryClient.setQueriesData<any[]>({ queryKey: ["email-conversations"] }, (old) => {
        if (!old) return old;
        return old.map((conv) => {
          if (conv.id !== variables.conversationId) return conv;

          if (variables.isThread && conv.thread_metadata) {
            return {
              ...conv,
              isRead: !variables.markAsUnread,
              unreadCount: variables.markAsUnread ? conv.thread_metadata.total_emails || 1 : 0,
              thread_metadata: {
                ...conv.thread_metadata,
                unread_count: variables.markAsUnread ? conv.thread_metadata.total_emails || 1 : 0,
              },
            };
          }

          return {
            ...conv,
            isRead: !variables.markAsUnread,
            unreadCount: variables.markAsUnread ? 1 : 0,
            read_date: variables.markAsUnread ? null : new Date().toISOString(),
          };
        });
      });

      // Optimistically update thread cache if applicable
      if (variables.isThread && variables.threadId) {
        const threadKey = ["email-thread", variables.threadId, variables.accountId];
        const previousThread = queryClient.getQueryData<any>(threadKey);

        if (previousThread) {
          queryClient.setQueryData(threadKey, (old: any) => {
            if (!old) return old;
            const updated = { ...old };
            if (updated.thread_metadata) {
              updated.thread_metadata = {
                ...updated.thread_metadata,
                unread_count: variables.markAsUnread
                  ? updated.thread_metadata.total_emails || 1
                  : 0,
              };
            }
            if (updated.thread_emails) {
              updated.thread_emails = updated.thread_emails.map((email: any) => ({
                ...email,
                read_date: variables.markAsUnread
                  ? null
                  : email.read_date || new Date().toISOString(),
              }));
            }
            return updated;
          });
        }

        return { previousConversations, previousThread, threadKey };
      }

      return { previousConversations };
    },
    onError: (_error, _variables, context) => {
      // Rollback conversations
      if (context?.previousConversations) {
        for (const [queryKey, data] of context.previousConversations) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      // Rollback thread
      if (context && "previousThread" in context && context.threadKey) {
        queryClient.setQueryData((context as any).threadKey, (context as any).previousThread);
      }
    },
  });

  return {
    updateReadStatus: updateReadStatusMutation.mutateAsync,
    isUpdating: updateReadStatusMutation.isPending,
    updateError: updateReadStatusMutation.error,
  };
}
